import {default as ReactDatePicker} from 'react-datepicker'
import InputGroup from 'react-bootstrap/InputGroup';
import {useCallback, useState} from 'react'
import th from 'date-fns/locale/th';
import {format, isValid, parse} from 'date-fns'
import {datePickerRawValue} from './datePickerRawValue'

const DatepickerInput = ({
  selectsRange,
  inputGroupClassName,
  enableTyping,
  inputError,
  iconName,
  size,
  ...inputProps
}: any) => {
  const rawValue = inputProps.value || ''
  const split = rawValue.split(' - ')
  let value = rawValue

  if (split.length === 2) {
    if (split[0] === split[1]) {
      value = split[0]
    }
  }

  return <div>
    <InputGroup size={size} style={selectsRange?{minWidth: 255}:{}} className={inputGroupClassName||"mb-3"}>
      <input
        type="text"
        {...inputProps}
        value={value}
        readOnly={!enableTyping}
        aria-invalid={Boolean(inputError)}
        className={`${inputProps.className || ''} ${inputError ? 'is-invalid' : ''}`.trim()}
      />
      <InputGroup.Text id="basic-addon2"><i className={'bi bi-'+ (iconName||'calendar-event') +' fs-7'}></i></InputGroup.Text>
    </InputGroup>
    {inputError && <div className="invalid-feedback d-block mt-n2 mb-2">{inputError}</div>}
  </div>
};

/**
 * แยกข้อความช่วงวันที่ที่ผู้ใช้พิมพ์เอง — รับ "01/01/2026 - 29/09/2026", "01/01/2026-29/09/2026"
 * (ขีดติดกัน ตามที่ลูกค้าพิมพ์จริง 29 ก.ย. 2569), "–" และ "ถึง" · ขีดติดกันแยกเฉพาะหลังปี 4 หลัก
 * จะได้ไม่ไปตัดรูปแบบวันที่ที่ใช้ขีดเอง
 */
const splitTypedRange = (value: string): string[] =>
  value
    .trim()
    .split(/\s+-\s+|\s*[–—]\s*|\s+ถึง\s+|(?<=\d{4})\s*-\s*(?=\d)/)
    .map((part) => part.trim())
    .filter(Boolean)

export default function DatePicker(props: any) {
  const name = props.name
  const [inputError, setInputError] = useState('')
  const dateFormats = Array.isArray(props.dateFormat)
    ? props.dateFormat
    : [props.dateFormat || 'dd/MM/yyyy']

  const parseDateText = useCallback((value: string): Date | null => {
    for (const dateFormat of dateFormats) {
      try {
        const parsed = parse(value, dateFormat, new Date(), {locale: th})
        if (isValid(parsed) && format(parsed, dateFormat, {locale: th}) === value) return parsed
      } catch {
        // ลองรูปแบบถัดไป
      }
    }
    return null
  }, [dateFormats.join('|')])

  const isValidDateText = useCallback((value: string) => parseDateText(value) !== null, [parseDateText])

  const validateTypedValue = useCallback((value: string) => {
    if (!value) {
      setInputError('')
      return
    }

    const dateValues = props.selectsRange ? splitTypedRange(value) : [value]
    const isValidValue = dateValues.length > 0 && dateValues.length <= 2 && dateValues.every((dateValue: string) => isValidDateText(dateValue))
    setInputError(isValidValue ? '' : `กรุณากรอกวันที่ให้ถูกต้อง (${dateFormats.join(' หรือ ')})`)
  }, [dateFormats.join('|'), isValidDateText, props.selectsRange])

  /**
   * ช่องแบบเลือกช่วง: react-datepicker ไม่แปลงข้อความช่วงที่พิมพ์เองเป็นวันที่ (แปลงได้แค่วันเดียว)
   * พิมพ์ครบสองวันแล้วส่ง [เริ่ม, สิ้นสุด] ออกไปเอง — วันกลับด้านสลับให้
   */
  const applyTypedRange = useCallback((value: string, event: React.SyntheticEvent<any>) => {
    const parts = splitTypedRange(value)
    if (parts.length !== 2) return
    const start = parseDateText(parts[0])
    const end = parseDateText(parts[1])
    if (!start || !end) return
    props.onChange(start <= end ? [start, end] : [end, start], event, name)
  }, [parseDateText, props.onChange, name])

  const onChangeHandler = useCallback(
  (date: Date, e: React.SyntheticEvent<any>) => {
    if (date) setInputError('')
    props.onChange(date, e, name)
  },
  [props.onChange, name]
)

  const onChangeRawHandler = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const value = datePickerRawValue(event)

    // react-datepicker also fires onChangeRaw for clicks inside the calendar.
    // Those events have no input value and must be left to onChangeHandler.
    if (value !== null) {
      validateTypedValue(value)
      if (props.selectsRange) applyTypedRange(value, event)
    }
    props.onChangeRaw?.(event)
  }, [props.onChangeRaw, props.selectsRange, validateTypedValue, applyTypedRange])

  return  (
    <ReactDatePicker
      {...props}
      className={"form-control"}
      onChange={onChangeHandler}
      onChangeRaw={onChangeRawHandler}
      strictParsing
      customInput={<DatepickerInput
          selectsRange={props.selectsRange}
         enableTyping={props.enableTyping ?? true}
         inputError={inputError}
         inputGroupClassName={props.inputGroupClassName}
         size={props.size}
         iconName={props.iconName} />}
      locale={th}
    />
  )
}
